import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import {
  confirmEmail,
  createUser,
  deleteUser,
  fetchUser,
  fetchUsers,
  setPassword,
  setUserActive,
  updateUser,
} from './api'

/**
 * The account directory.
 *
 * Every mutation invalidates the whole `users` tree rather than the one row: the
 * two tiles above the table are counts over the same collection, and leaving
 * them stale shows six active accounts above a table listing five. Same reason
 * the demo screen's `useSetPersonStatus` did it, and the reason has not changed
 * for being real.
 */

/**
 * One page of the directory.
 *
 * `placeholderData` keeps the previous page under the reader while the next one
 * loads, which is safe here in a way it was not on the fusion families: this is
 * one collection with one subject, and every control over it only narrows or
 * reorders — none of them can swap what the table is a list of.
 */
export function useUsers(params, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.users(params),
    queryFn: ({ signal }) => fetchUsers(params, signal),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    /* The caller's gate. Work Orders reads the directory only to fill an
       assignee picker, so it asks for it when a form opens and not on every
       visit to the board. */
    enabled,
  })
}

/**
 * Names by id, for screens that hold only an id — a work order's `assignee_ids`
 * is the case that needs it. The list read is open to any signed-in user, so no
 * gate. Inactive accounts are included: somebody deactivated after being
 * assigned is still the assignee, and reading as "Unassigned" would be false.
 *
 * One page at the server's cap. A directory past 200 leaves the rest unnamed,
 * which callers show as an unnamed assignee rather than as nobody.
 */
const DIRECTORY = { pageSize: 200, sortBy: 'first_name', sortDir: 'asc' }

export function useUserNames() {
  const { data } = useQuery({
    queryKey: queryKeys.users(DIRECTORY),
    queryFn: ({ signal }) => fetchUsers(DIRECTORY, signal),
    staleTime: 5 * 60_000,
    select: (page) => new Map(page.items.map((person) => [person.id, person.name || person.email])),
  })
  return data
}

/**
 * One person, with their role grants and shift assignments.
 *
 * Read when the editor opens and not before: the list carries none of it, and
 * prefetching a page of detail reads to fill a drawer somebody may not open is
 * work proportional to the page.
 *
 * `enabled` on the id so the same hook serves the create case, where there is
 * nobody to read.
 */
export function useUser(id) {
  return useQuery({
    queryKey: queryKeys.user(id),
    queryFn: ({ signal }) => fetchUser(id, signal),
    enabled: Boolean(id),
    /* Not held: the editor must open on what the server holds NOW, because the
       PUT it will send replaces every grant with what the form shows. A stale
       read here would revoke a role somebody was given in the meantime. */
    staleTime: 0,
    retry: false,
  })
}

/**
 * Invite one.
 *
 * Not retried, and this one matters more than most: a create that succeeded and
 * merely failed to report would send a second invitation email to a real person,
 * and the second request would then fail on the duplicate address — leaving a
 * created account behind an error message.
 */
export function useCreateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createUser,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: updateUser,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  })
}

/**
 * Suspend or reinstate from the row menu.
 *
 * Two requests, not one — a read then a full-replace PUT. There is no PATCH, and
 * building the PUT from the table row instead would send `roles: []` and revoke
 * every grant the person holds as a side effect of deactivating them. See the
 * api module.
 */
export function useSetUserActive() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: setUserActive,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useDeleteUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: deleteUser,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  })
}

/**
 * The two public token calls.
 *
 * No cache to invalidate: nobody is signed in when either runs, and there is no
 * session query for them to touch. Neither is retried — an expired or spent
 * token is an answer, and the second attempt gets the same one.
 */
export function useSetPassword() {
  return useMutation({ mutationFn: setPassword, retry: false })
}

export function useConfirmEmail() {
  return useMutation({ mutationFn: confirmEmail, retry: false })
}
